/**
 * **J-3271 · Money · By visit** — `docs/visit-page.md` §16.4, Board 12 C: the
 * job's Money tab carries this card and nothing else. *every cost posts to a
 * visit; the job is the sum* — a row per visit (*Visit · Est h · Actual ·
 * Labor · Materials · Equipment · Fees · GM*), the visit you came from on the
 * green band (`?visit=<id>`, the closed banner's *locked into J-3271*), dashes
 * where nothing is posted. A record table drawn by this page's own component —
 * never the shared list engine (do-not-substitute 0).
 *
 * Every figure is the server's (`GET /jobs/:id/money/by-visit`).
 */
import { useEffect, useState } from 'react'
import type { JobByVisit, JobByVisitRow } from '@rowted/shared'
import { request } from '../lib/api.js'
import { cn } from '../lib/cn.js'
import { CLOSE, COMMON, HEADER, RAIL } from './copy.js'
import { dayRange, dayText, hours1, money, percent } from './format.js'
import { VisitMark } from './visit-mark.js'

const GRID = 'grid grid-cols-[1fr_52px_56px_76px_82px_82px_64px_54px] gap-2.5 px-4'
const WORDS = CLOSE.byVisit

export function JobByVisitCard({ jobId, highlight, onOpen }: {
  jobId: string
  /** The visit to put on the green band — the one the page was opened from. */
  highlight?: string | null
  onOpen: (href: string) => void
}) {
  const [data, setData] = useState<JobByVisit | null>(null)
  const [problem, setProblem] = useState<string | null>(null)
  useEffect(() => {
    let live = true
    request<JobByVisit>(`/jobs/${encodeURIComponent(jobId)}/money/by-visit`)
      .then((answer) => { if (live) setData(answer) })
      .catch((cause: unknown) => { if (live) setProblem(cause instanceof Error ? cause.message : String(cause)) })
    return () => { live = false }
  }, [jobId])

  if (problem) return <p className="m-0 text-[12.5px] text-danger">{problem}</p>
  if (!data) return <div className="h-[136px] rounded-[14px] border border-line bg-surface" data-by-visit-loading="" />
  return (
    <div className="overflow-hidden rounded-[14px] border border-line bg-surface leading-[normal] tabular-nums" data-as-drawn="vp-12 C by-visit">
      <div className="flex items-center gap-2 border-b border-line px-4 py-3">
        <b className="text-[14px] font-bold">{WORDS.title(data.job.reference)}</b>
        <span className="text-[12px] text-text-muted">{WORDS.sub}</span>
      </div>
      {/* Board 12 C's heights are inside the rule under them: the head 32 and a row 52, each and its line. */}
      <div className={cn(GRID, 'h-[33px] items-center border-b border-line bg-surface-alt text-[10.5px] font-semibold uppercase tracking-[.05em] text-text-faint')}>
        <span>{WORDS.head.visit}</span>
        <span className="text-right">{WORDS.head.estH}</span>
        <span className="text-right">{WORDS.head.actual}</span>
        <span className="text-right">{WORDS.head.labor}</span>
        <span className="text-right">{WORDS.head.materials}</span>
        <span className="text-right">{WORDS.head.equipment}</span>
        <span className="text-right">{WORDS.head.fees}</span>
        <span className="text-right">{WORDS.head.gm}</span>
      </div>
      {data.rows.map((row, index) => (
        <ByVisitRow key={row.id} row={row} last={index === data.rows.length - 1}
          highlighted={highlight === row.id || highlight === row.reference} onOpen={onOpen} />
      ))}
    </div>
  )
}

function ByVisitRow({ row, last, highlighted, onOpen }: {
  row: JobByVisitRow; last: boolean; highlighted: boolean; onOpen: (href: string) => void
}) {
  const figure = (value: string | null, bold = false) => (value === null
    ? <span className="whitespace-nowrap text-right text-[12.5px] text-text-faint">{'—'}</span>
    : <span className={cn('whitespace-nowrap text-right text-[12.5px] text-text', bold && 'font-bold')}>{value}</span>)
  return (
    <button
      type="button"
      onClick={() => onOpen(`/visits/${row.id}`)}
      className={cn(GRID, 'w-full items-center text-left text-[12.5px] hover:bg-hover',
        last ? 'h-[52px]' : 'h-[53px] border-b border-line-faint', highlighted && 'bg-green-band-bg')}
      data-by-visit-row={row.reference}
    >
      <div className="flex min-w-0 items-center gap-2">
        <VisitMark mark={row.mark} size={28} />
        {/* Board 12 C wraps a long name or line inside the row's 52, never cuts it short. */}
        <div className="flex min-w-0 flex-col">
          <b className="text-[12.5px] font-bold">{row.name}</b>
          <span className="text-[11px] text-text-muted">{lineOf(row)}</span>
        </div>
      </div>
      {figure(hours1(row.estimatedHours))}
      {figure(row.actualHours === null ? null : hours1(row.actualHours), true)}
      {figure(row.cost ? money(row.cost.labor) : null)}
      {figure(row.cost ? money(row.cost.materials) : null)}
      {figure(row.cost ? money(row.cost.equipment) : null)}
      {figure(row.cost ? money(row.cost.fees) : null)}
      {row.margin === null
        ? figure(null)
        : <span className={cn('text-right font-bold', row.margin.tone === 'good' ? 'text-accent-dark' : 'text-danger')}>{percent(row.margin.percent)}</span>}
    </button>
  )
}

/** *V-1187 · Oct 21 – 23 · closed* · *V-1188 · 1 day · not scheduled · from Apr 15*. */
function lineOf(row: JobByVisitRow): string {
  const { days } = row
  const when = days.first
    ? (days.count > 1 && days.last ? dayRange(days.first, days.last, 'short') : dayText(days.first, 'date'))
    : null
  if (row.status === 'cancelled') return COMMON.dot(row.reference, RAIL.jobVisits.cancelled)
  if (row.status === 'closed') return when ? WORDS.lineClosed(row.reference, when) : COMMON.dot(row.reference, HEADER.status.closed)
  if (row.status === 'on_hold') return COMMON.dot(row.reference, HEADER.status.on_hold)
  if (!when) {
    const count = days.count === 1 ? WORDS.oneDay : WORDS.nDays(String(days.count))
    return days.from ? WORDS.lineUnscheduled(row.reference, count, dayText(days.from, 'monthDay')) : COMMON.dot(row.reference, count)
  }
  return days.todayIndex !== null
    ? RAIL.jobVisits.lineToday(row.reference, when, String(days.todayIndex), String(days.count))
    : COMMON.dot(row.reference, when)
}
